import React, { useRef, useEffect, useState } from 'react';
import gsap from 'gsap';
import { Link, useNavigate } from 'react-router-dom';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import evento from '../../assets/evento1.webp';
import petty from '../../assets/petty1.webp';
import wayfare from '../../assets/way.webp';
import verd from '../../assets/verd2.webp';
import nuvex from '../../assets/nuvexcard.webp';

import './../styles/Works.css';

// ── 1. Import Sound Controller & Audio Assets ──
import { sounds } from '../Utils/soundEffects';
import switchToggleSfx from '../../assets/sounds/mood-switch.mp3';
import workCardHoverSfx from '../../assets/sounds/mini-click.mp3';
import exploreClickSfx from '../../assets/sounds/work-tap.mp3';

gsap.registerPlugin(ScrollTrigger);

// 1. Default Arched Fan Deck Configuration
const fanConfigs = [
  { rotate: -22, x: -400, y: 60, zIndex: 1, scale: 0.8 },
  { rotate: -10, x: -210, y: 15, zIndex: 2, scale: 0.91 },
  { rotate: 0,   x: 0,    y: 0,  zIndex: 4, scale: 1    }, // Center card
  { rotate: 10,  x: 210,  y: 15, zIndex: 2, scale: 0.91 },
  { rotate: 22,  x: 400,  y: 60, zIndex: 1, scale: 0.8 },
];

// 2. Almost Horizontal Diagonal Line Configuration
const diagonalConfigs = [
  { rotate: 0, x: -420, y: -45, zIndex: 1, scale: 0.88 },
  { rotate: 0, x: -210, y: -22, zIndex: 2, scale: 0.88 },
  { rotate: 0,  x: 0,    y: 0,   zIndex: 3, scale: 0.88 },
  { rotate: 0, x: 210,  y: 22,  zIndex: 4, scale: 0.88 },
  { rotate: 0,  x: 420,  y: 45,  zIndex: 5, scale: 0.88 },
];

const works = [
  {
    id: 'travel',
    category: 'uiux',
    img: wayfare,
    tag: 'UX Case Study',
    title: 'WayFare Travel App',
    desc: 'UX case study exploring personalized trip planning and organization.',
    priority: false,
  },
  {
    id: 'bakery',
    category: 'graphics',
    img: petty,
    tag: 'Branding Design',
    title: 'Petty Pastries Branding',
    desc: 'Branding identity designed for a modern pastry business.',
    priority: true,
  },
  {
    id: 'fintech',
    category: 'frontend',
    img: nuvex,
    tag: 'Frontend',
    title: 'Ayurveda E-commerce Website',
    desc: 'Dynamic e-commerce platform built with ReactJS and JSON data handling.',
    priority: false,
  },
  {
    id: 'event',
    category: 'uiux',
    img: evento,
    tag: 'App Design',
    title: 'Research Project',
    desc: 'Sustainable drone surveillance system with eternal vertex cover.',
    priority: false,
  },
  {
    id: 'verd',
    category: 'graphics',
    img: verd,
    tag: 'Visual Design',
    title: 'Your Project Title',
    desc: 'Your project description goes here.',
    priority: false,
  },
];

const Works = () => {
  const worksRef = useRef(null);
  const cardsRef = useRef([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const isDarkRef = useRef(false);
  const navigate = useNavigate();

  // ── 2. Preload Sound Buffers on Mount ──
  useEffect(() => {
    sounds.loadSound('switchToggle', switchToggleSfx);
    sounds.loadSound('workCardHover', workCardHoverSfx);
    sounds.loadSound('exploreClick', exploreClickSfx);

    const unlockAudio = () => {
      sounds.init();
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };

    window.addEventListener('pointerdown', unlockAudio);
    window.addEventListener('keydown', unlockAudio);

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  // Keep ref synchronized for hover calculations
  useEffect(() => {
    isDarkRef.current = isDarkMode;
  }, [isDarkMode]);

  // Smooth blur dissolve theme switch + Card Rearrangement Morph
  const handleToggleTheme = () => {
    // 3. Play toggle switch sound immediately
    sounds.play('switchToggle', 0.35);

    const el = worksRef.current;
    const cards = cardsRef.current.filter(Boolean);
    const nextDarkState = !isDarkMode;

    if (!el) {
      setIsDarkMode(nextDarkState);
      return;
    }

    // Morph cards between Fan and Diagonal layouts
    const targetConfigs = nextDarkState ? diagonalConfigs : fanConfigs;

    gsap.timeline()
      .to(el, {
        filter: 'blur(12px)',
        opacity: 0.88,
        duration: 0.28,
        ease: 'power2.in',
        onComplete: () => {
          setIsDarkMode(nextDarkState);
        },
      })
      .to(cards, {
        rotate: (i) => targetConfigs[i]?.rotate ?? 0,
        x: (i) => targetConfigs[i]?.x ?? 0,
        y: (i) => targetConfigs[i]?.y ?? 0,
        scale: (i) => targetConfigs[i]?.scale ?? 0.88,
        zIndex: (i) => targetConfigs[i]?.zIndex ?? i + 1,
        duration: 0.7,
        stagger: 0.03,
        ease: 'power3.out',
        overwrite: 'auto',
      }, '-=0.1')
      .to(el, {
        filter: 'blur(0px)',
        opacity: 1,
        duration: 0.45,
        ease: 'power2.out',
      }, '-=0.5');
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      // Desktop Entrance Animation
      mm.add('(min-width: 1001px)', () => {
        const cards = cardsRef.current.filter(Boolean);

        gsap.set(cards, {
          rotate: 0,
          x: 0,
          y: 0,
          scale: 0.75,
          opacity: 0,
          zIndex: 1,
          force3D: true,
          transformOrigin: 'bottom center',
        });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: worksRef.current,
            start: 'top 75%',
            end: 'bottom 20%',
            toggleActions: 'restart pause resume reverse',
          },
        });

        tl.fromTo(
          '.workanim .word, .works-toggle-wrapper',
          { opacity: 0, y: 35, filter: 'blur(6px)' },
          {
            opacity: 1,
            y: 0,
            filter: 'blur(0px)',
            duration: 0.8,
            ease: 'power3.out',
            stagger: 0.1,
          }
        );

        tl.to(cards, {
          opacity: 1,
          rotate: (i) => (isDarkRef.current ? diagonalConfigs[i]?.rotate : fanConfigs[i]?.rotate) ?? 0,
          x: (i) => (isDarkRef.current ? diagonalConfigs[i]?.x : fanConfigs[i]?.x) ?? 0,
          y: (i) => (isDarkRef.current ? diagonalConfigs[i]?.y : fanConfigs[i]?.y) ?? 0,
          scale: (i) => (isDarkRef.current ? diagonalConfigs[i]?.scale : fanConfigs[i]?.scale) ?? 1,
          zIndex: (i) => (isDarkRef.current ? diagonalConfigs[i]?.zIndex : fanConfigs[i]?.zIndex) ?? 1,
          duration: 0.9,
          ease: 'power3.out',
          stagger: 0.04,
          force3D: true,
        }, '-=0.4');

        tl.fromTo(
          '.works-subtext, .center-button',
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', stagger: 0.1 },
          '-=0.3'
        );
      });

      // Mobile Entrance
      mm.add('(max-width: 1000px)', () => {
        gsap.from('.workanim .word, .works-toggle-wrapper, .works-subtext, .center-button', {
          scrollTrigger: {
            trigger: worksRef.current,
            start: 'top 85%',
            once: true,
          },
          opacity: 0,
          y: 20,
          duration: 0.5,
          stagger: 0.08,
          ease: 'power2.out',
        });
      });
    }, worksRef);

    return () => ctx.revert();
  }, []);

  const handleMouseEnter = (index) => {
    if (window.innerWidth <= 1000) return;

    // 4. Play hover sound for work card
    sounds.play('workCardHover', 0.25);

    const card = cardsRef.current[index];
    const cfg = isDarkRef.current ? diagonalConfigs[index] : fanConfigs[index];
    if (!card || !cfg) return;

    gsap.to(card, {
      y: cfg.y - 20,
      scale: cfg.scale + 0.04,
      duration: 0.25,
      ease: 'power2.out',
      overwrite: 'auto',
      force3D: true,
    });
  };

  const handleMouseLeave = (index) => {
    if (window.innerWidth <= 1000) return;
    const card = cardsRef.current[index];
    const cfg = isDarkRef.current ? diagonalConfigs[index] : fanConfigs[index];
    if (!card || !cfg) return;

    gsap.to(card, {
      y: cfg.y,
      scale: cfg.scale,
      duration: 0.3,
      ease: 'power2.inOut',
      overwrite: 'auto',
      force3D: true,
    });
  };

  const handleProjectClick = (projectItem) => {
    navigate('/work', {
      state: {
        activeCard: projectItem.category,
        projectId: projectItem.id,
      },
    });
  };

  // 5. Handle Explore Button Click with audio
  const handleExploreClick = () => {
    sounds.play('exploreClick', 0.4);
  };

  return (
    <div className={`works-wrapper-outer ${isDarkMode ? 'dark-mode-active' : ''}`}>
      <div className="my-work" ref={worksRef}>
        {/* Title + Toggle Switch */}
        <div className="workanim">
          <div className="works-title-line">
            <h6>
              <span className="word">Work</span>
              <span className="word diff">Archive</span>
            </h6>

            <div className="works-toggle-wrapper">
              <button
                type="button"
                role="switch"
                aria-checked={isDarkMode}
                aria-label="Toggle dark background"
                className={`works-glass-switch ${isDarkMode ? 'active' : ''}`}
                onClick={handleToggleTheme}
              >
                <span className="works-switch-thumb" />
              </button>
            </div>
          </div>
        </div>

        {/* Dynamic Card Container */}
        <div className={`work-fan-container ${isDarkMode ? 'diagonal-mode' : ''}`}>
          {works.map((work, i) => (
            <div
              key={work.id}
              className="work-item"
              ref={(el) => (cardsRef.current[i] = el)}
              data-fan-index={i}
              onMouseEnter={() => handleMouseEnter(i)}
              onMouseLeave={() => handleMouseLeave(i)}
              onClick={() => handleProjectClick(work)}
            >
              <div className="image-wrapper">
                <img
                  src={work.img}
                  alt={work.title}
                  className="work-img"
                  width="290"
                  height="290"
                  loading={work.priority ? 'eager' : 'lazy'}
                  decoding={work.priority ? 'sync' : 'async'}
                  fetchPriority={work.priority ? 'high' : 'low'}
                />
                <div className="pill-tag">
                  <h5>{work.tag}</h5>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Subtext */}
        <p className="works-subtext">
          A handpicked selection of brand identities, user interfaces, and full-stack creations crafted with intention and precision.
        </p>

        {/* Inverted CTA Button with Sound */}
        <div className="center-button">
          <Link to="/work" className="no-underline" onClick={handleExploreClick}>
            <button type="button">Explore</button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Works;